/**
 * V14.2 — Archived Batches list. Super-admin can restore or hard-delete.
 * Hidden from learners + instructors entirely (backend gates on role).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ArchiveRestore, Trash2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth-context';

export default function ArchivedBatches() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const isSuperAdmin = user?.role === 'superadmin';

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/batches', { params: { status: 'archived', limit: 100 } });
      setBatches(res.data?.batches || []);
    } catch (e) {
      toast.error('Could not load archived batches');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const restore = async (b) => {
    if (!window.confirm(`Restore "${b.name}"? Cancelled sessions will not be re-instated; admin must re-create as needed.`)) return;
    try {
      await api.post(`/api/batches/${b.id}/restore`);
      toast.success('Batch restored to active');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore');
    }
  };

  const hardDelete = async (b) => {
    if (!window.confirm(`Permanently delete "${b.name}"? This action cannot be undone.`)) return;
    try {
      await api.delete(`/api/batches/${b.id}`, { params: { hard: true } });
      toast.success('Batch permanently deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="archived-batches-page">
      <header className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/batches')}>
          <ArrowLeft className="h-4 w-4 mr-1" />Back to batches
        </Button>
        <div>
          <h1 className="font-heading text-3xl">Archived batches</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Soft-deleted batches. Hidden from learners. Super-admin can restore or permanently delete.
          </p>
        </div>
      </header>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg font-heading">{batches.length} archived</CardTitle>
        </CardHeader>
        <CardContent className="px-2 sm:px-6">
          {loading ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
          ) : batches.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No archived batches.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden sm:table-cell">Stage</TableHead>
                    <TableHead className="hidden md:table-cell">Type</TableHead>
                    <TableHead className="hidden md:table-cell">Archived</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batches.map((b) => (
                    <TableRow key={b.id} data-testid={`archived-batch-${b.id}`}>
                      <TableCell className="font-medium">{b.name}</TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <Badge variant="outline" className="capitalize">
                          {b.course_type === 'ttc' ? 'TTC' : b.course_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-muted-foreground capitalize">
                        {b.batch_type === 'one_on_one' ? '1:1' : 'Group'}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                        {b.archived_at ? new Date(b.archived_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline"
                                  onClick={() => restore(b)}
                                  data-testid={`restore-batch-${b.id}`}>
                            <ArchiveRestore className="h-3.5 w-3.5 mr-1" />Restore
                          </Button>
                          {isSuperAdmin && (
                            <Button size="sm" variant="outline"
                                    className="text-rose-700 border-rose-200 hover:bg-rose-50"
                                    onClick={() => hardDelete(b)}
                                    data-testid={`hard-delete-batch-${b.id}`}>
                              <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
